import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';

/**
 * PhotoSlot — Iter 190h.
 * Every team-member portrait container is wrapped in a PhotoSlot so vendors
 * can DOUBLE-CLICK on the slot (even when the photo is empty) to open the
 * image picker. Previously the raw gradient-filled `<div>` had no interaction
 * hook, forcing users into the drawer's URL input.
 *
 * The slot dispatches the same `wb:edit-image` CustomEvent that SafeImg
 * dispatches natively — including a full field path like
 * `members.2.photo`, which DashboardContent's `setImage` handler now supports
 * for arbitrary-depth nested writes.
 */
function PhotoSlot({ block, field, src, alt, className = '', style, children, rounded = 'rounded-full' }) {
  const ref = React.useRef(null);
  const openEditor = (e) => {
    e.stopPropagation();
    const el = ref.current;
    if (!el || !block?.id || !field) return;
    const r = el.getBoundingClientRect();
    window.dispatchEvent(new CustomEvent('wb:edit-image', {
      detail: { blockId: block.id, field, rect: { top: r.top, left: r.left, width: r.width, height: r.height } },
    }));
  };
  return (
    <div
      ref={ref}
      onDoubleClick={openEditor}
      className={`${className} relative overflow-hidden cursor-pointer group/photo`}
      style={style}
      data-wb-image="1"
      data-wb-image-field={field}
      data-testid={`team-photo-slot-${field}`}
      title="Double-click to change photo"
    >
      {src
        ? <SafeImg src={src} alt={alt || ''} field={field} className={`w-full h-full object-cover ${rounded === 'rounded-none' ? '' : rounded}`} />
        : (children || <div className="w-full h-full bg-gradient-to-br from-violet-300 to-fuchsia-400" />)}
      {/* Hover hint — the pencil chip signals the slot is editable */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/photo:opacity-100 transition-opacity bg-black/30 pointer-events-none">
        <span className="text-white text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-full bg-black/60">Double-click</span>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * TEAM — new variants
 *   • palm-leaf-bg · square-cards · violet-bg           (Iter 175c)
 *   • org-chart-tree · founder-spotlight                (Iter 189c Phase B)
 * Every member's name/role and the section title/tagline are inline-editable.
 * Iter 190h — Every team photo now supports double-click-to-upload.
 * ════════════════════════════════════════════════════════════════════════ */
export function TeamExt({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const layout = block.layout;
  const sourceField = Array.isArray(c.members) ? 'members' : 'items';
  const members = c[sourceField] || Array.from({length:6}).map((_,i)=>({ name:`Team ${i+1}`, role:'Designer' }));

  const photoField = (i) => `${sourceField}.${i}.photo`;

  const renderName = (m, i, light) => (
    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.name`} value={m.name} tag="p" className="font-bold text-sm" style={{ color: light ? '#fff' : theme.ink }} placeholder="Name" />
  );
  const renderRole = (m, i, light) => (
    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.role`} value={m.role || ''} tag="p" className="text-xs opacity-70 uppercase tracking-wider" style={{ color: light ? '#fff' : theme.ink }} placeholder="Role" />
  );

  if (layout === 'palm-leaf-bg') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="relative px-6 py-24 overflow-hidden">
        {c.background_image ? <SafeImg src={c.background_image} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-gradient-to-br from-emerald-700 to-teal-900" />}
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 max-w-4xl mx-auto bg-white p-10 rounded-2xl" style={{ borderRadius: theme.radius || 16 }}>
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Our Crew'} tag="h2" className="text-3xl font-bold text-center" style={{ fontFamily: theme.headingFont, color: theme.ink }} placeholder="Headline" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8">
            {members.slice(0,3).map((m,i)=>(
              <div key={i} className="text-center">
                <PhotoSlot block={block} field={photoField(i)} src={m.photo} alt={m.name} className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-violet-300 to-fuchsia-400" />
                <div className="mt-3">{renderName(m,i,false)}{renderRole(m,i,false)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'square-cards') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto text-center">
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Better crew for your startup'} tag="h2" className="text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || ''} tag="p" className="mt-3 opacity-70 max-w-xl mx-auto" placeholder="Tagline" />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            {members.slice(0,8).map((m,i)=>(
              <div key={i}>
                <PhotoSlot block={block} field={photoField(i)} src={m.photo} alt={m.name} className="aspect-square bg-gradient-to-br from-gray-300 to-gray-500" rounded="rounded-none" />
                <div className="mt-3 text-left">{renderName(m,i,false)}{renderRole(m,i,false)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'violet-bg') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: '#3B1F8B' }}>
        <div className="max-w-5xl mx-auto text-white text-center">
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Startup Crew'} tag="h2" className="text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || ''} tag="p" className="mt-3 opacity-80 max-w-xl mx-auto" placeholder="Tagline" />
          <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-6">
            {members.slice(0,5).map((m,i)=>(
              <div key={i} className="text-center">
                <PhotoSlot block={block} field={photoField(i)} src={m.photo} alt={m.name} className="w-20 h-20 mx-auto rounded-full bg-white/15" />
                <div className="mt-3">{renderName(m,i,true)}{renderRole(m,i,true)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── org-chart-tree · Iter 189c Phase B ─────────────────────────────
  //  A hand-drawn leadership tree: leader on top, direct reports fanned
  //  underneath, connected by hairline SVG lines. Great for company /
  //  studio "who reports to whom" pages.
  if (layout === 'org-chart-tree') {
    const leader = members[0] || { name: 'Alex Rivera', role: 'Founder & CEO' };
    const reports = members.slice(1, 5).length ? members.slice(1, 5) : [
      { name: 'Sarah Chen',   role: 'Head of Design'      },
      { name: 'Marcus Doyle', role: 'Head of Engineering' },
      { name: 'Priya Kapoor', role: 'Head of Operations'  },
      { name: 'Jordan Blake', role: 'Head of Growth'      },
    ];
    return (
      <section data-block-type={block.type} data-layout={block.layout || 'default'} className="px-6 py-24" style={{ background: theme.bg || '#FAF6EE', color: theme.ink || '#0E0E23' }}>
        <div className="max-w-6xl mx-auto text-center">
          <E block={block} onEdit={onEdit} field="eyebrow" value={c.eyebrow || 'LEADERSHIP'} tag="p" className="text-xs uppercase tracking-[0.24em] opacity-60" placeholder="Eyebrow" />
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'The people steering the ship'} tag="h2" className="mt-2 text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || ''} tag="p" className="mt-3 opacity-70 max-w-xl mx-auto" placeholder="Tagline" />

          {/* Leader node */}
          <div className="mt-14 flex flex-col items-center relative">
            <PhotoSlot
              block={block}
              field={photoField(0)}
              src={leader.photo}
              alt={leader.name}
              className="w-32 h-32 rounded-full ring-4"
              style={{ ringColor: theme.accent || '#8B5CF6', boxShadow: `0 0 0 4px ${theme.accent || '#8B5CF6'}` }}
            >
              <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${theme.accent || '#8B5CF6'}, #EC4899)` }} />
            </PhotoSlot>
            <div className="mt-4">
              {renderName(leader, 0, false)}
              {renderRole(leader, 0, false)}
            </div>

            {/* Trunk connector */}
            <div className="mt-8 mb-2" style={{ width: 2, height: 40, background: theme.ink ? `${theme.ink}22` : '#0E0E2322' }} />
            {/* Horizontal bar */}
            <div className="w-full max-w-3xl" style={{ height: 2, background: theme.ink ? `${theme.ink}22` : '#0E0E2322' }} />
          </div>

          {/* Reports */}
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {reports.map((m, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="w-1" style={{ height: 24, background: theme.ink ? `${theme.ink}22` : '#0E0E2322' }} />
                <PhotoSlot block={block} field={photoField(i + 1)} src={m.photo} alt={m.name} className="w-20 h-20 rounded-full mt-1" style={{ background: `${theme.accent || '#8B5CF6'}22` }} />
                <div className="mt-3 text-center">
                  {renderName(m, i + 1, false)}
                  {renderRole(m, i + 1, false)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── founder-spotlight · Iter 189c Phase B ──────────────────────────
  //  Editorial founder spotlight: large portrait + quote/bio on one side,
  //  a compact stacked list of teammates on the other. Feels magazine-y.
  if (layout === 'founder-spotlight') {
    const founder = members[0] || {
      name:  'Amelia Ross',
      role:  'Founder & Creative Director',
      quote: 'We build what we wish existed. Everything else follows from that.',
    };
    const rest = members.slice(1, 5).length ? members.slice(1, 5) : [
      { name: 'Rae Nakamura',  role: 'Lead Designer'   },
      { name: 'Idris Okafor',  role: 'Senior Engineer' },
      { name: 'Nina Whitecrow',role: 'Client Partner'  },
      { name: 'Sam Ibarra',    role: 'Producer'        },
    ];
    return (
      <section data-block-type={block.type} data-layout={block.layout || 'default'} className="px-6 py-24" style={{ background: theme.bg || '#FAF6EE', color: theme.ink || '#0E0E23' }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          {/* Portrait */}
          <div className="md:col-span-5">
            <PhotoSlot
              block={block}
              field={photoField(0)}
              src={founder.photo || 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&q=80'}
              alt={founder.name}
              className="aspect-[4/5] w-full"
              rounded="rounded-none"
              style={{ borderRadius: theme.radius || 0, background: `${theme.accent || '#8B5CF6'}22` }}
            />
            <div className="mt-5">
              {renderName(founder, 0, false)}
              {renderRole(founder, 0, false)}
            </div>
          </div>

          {/* Editorial column */}
          <div className="md:col-span-7">
            <E block={block} onEdit={onEdit} field="eyebrow" value={c.eyebrow || 'FOUNDER SPOTLIGHT'} tag="p" className="text-xs uppercase tracking-[0.24em]" style={{ color: theme.accent || '#8B5CF6' }} placeholder="Eyebrow" />
            <E block={block} onEdit={onEdit} field="title" value={c.title || 'A studio built on quiet obsession.'} tag="h2" className="mt-3 text-4xl md:text-5xl font-bold leading-tight" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
            <E
              block={block}
              onEdit={onEdit}
              field={sourceField + '.0.quote'}
              value={founder.quote || 'We build what we wish existed. Everything else follows from that.'}
              tag="blockquote"
              className="mt-6 text-lg italic opacity-80 border-l-2 pl-4"
              style={{ borderColor: theme.accent || '#8B5CF6', fontFamily: 'Georgia, serif' }}
              placeholder="Founder quote"
            />
            <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || 'Meet the small team behind every project. Multi-disciplinary, opinionated, and unafraid of a blank canvas.'} tag="p" className="mt-6 opacity-70" placeholder="Tagline" />

            {/* Rest of team — compact rows */}
            <div className="mt-10 divide-y" style={{ borderColor: `${theme.ink || '#0E0E23'}15` }}>
              {rest.map((m, i) => (
                <div key={i} className="flex items-center gap-4 py-4 border-t" style={{ borderColor: `${theme.ink || '#0E0E23'}15` }}>
                  <PhotoSlot block={block} field={photoField(i + 1)} src={m.photo} alt={m.name} className="w-12 h-12 rounded-full flex-shrink-0" style={{ background: `${theme.accent || '#8B5CF6'}22` }} />
                  <div className="flex-1">
                    {renderName(m, i + 1, false)}
                    {renderRole(m, i + 1, false)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return null;
}
